import { useEffect } from 'react'

const NO_SON_CAMPOS = ['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'file']

const esCampo = (el: unknown): el is HTMLElement =>
  el instanceof HTMLTextAreaElement ||
  (el instanceof HTMLInputElement && !NO_SON_CAMPOS.includes(el.type)) ||
  (el instanceof HTMLElement && el.isContentEditable)

/** Con menos que esto, no es el teclado: es la barra del navegador que se esconde. */
const UMBRAL_TECLADO = 120

/**
 * Qué parte de la página se ve y cuánto queda tapado debajo. El teclado está
 * abierto cuando lo visible se achica, esté donde esté: en el iPhone la vista
 * se corre hacia abajo y lo tapado puede dar cero con el teclado abierto
 * (OBS-009, E-12).
 */
export function medirTeclado(
  vista: { height: number; offsetTop: number },
  altoVentana: number,
  /** El alto de la ventana sin teclado: Chrome en iOS achica la ventana entera. */
  altoSinTeclado = altoVentana,
) {
  // Lo tapado se mide contra la ventana actual: si la ventana ya se achicó,
  // el teclado no tapa nada de ella.
  const tapado = Math.max(0, Math.round(altoVentana - vista.height - vista.offsetTop))
  const abierto = altoSinTeclado - vista.height >= UMBRAL_TECLADO
  return { tapado, visible: Math.round(vista.height), abierto }
}

/** Aire entre el campo y el borde de lo que se ve. */
const MARGEN = 12

/** El primer contenedor con desplazamiento propio (el cuerpo del panel). */
function contenedorConScroll(el: HTMLElement): HTMLElement | null {
  for (let p = el.parentElement; p; p = p.parentElement) {
    const { overflowY } = getComputedStyle(p)
    if (/(auto|scroll)/.test(overflowY) && p.scrollHeight > p.clientHeight) return p
  }
  return null
}

/** Cuánto se espera sin cambios para dar por terminado el movimiento del teclado. */
const QUIETO_MS = 150

/**
 * En el celular, el teclado no tapa el campo en el que se escribe (revisión OO
 * del 23/09/2026: "al tocar un campo se abre el teclado y tapa la vista").
 *
 * El teclado no achica la página: achica sólo lo visible (`visualViewport`), y
 * en el iPhone además la corre hacia arriba. Se publica cuánto tapa en
 * `--alto-teclado` y cuánto se ve en `--alto-visible`: la hoja de los modales
 * se apoya justo encima del teclado y mide lo que queda a la vista.
 *
 * OBS-009 (E-11, iPhone): al cerrar el teclado la hoja iba y venía. Se medía
 * sólo cuando lo visible cambiaba de alto, no cuando el iPhone lo corría, así
 * que la hoja quedaba en una posición vieja; y en cada cambio se llevaba el
 * campo al centro con un desplazamiento suave que se peleaba con el del
 * teléfono. Ahora se mide también al correrse, en el cuadro siguiente, y el
 * campo se acomoda una sola vez, cuando el teclado terminó de abrir y sólo si
 * quedó tapado. Al cerrar el teclado no se desplaza nada. En escritorio no
 * hace nada.
 *
 * OBS-009 (E-12, iPhone): la hoja seguía sin moverse y el campo no se veía
 * hasta escribir. Al abrir el teclado el iPhone corre la vista hasta el fondo
 * de la página, y "lo que tapa el teclado" daba cero: se medía el teclado por
 * eso. Ahora el teclado está abierto cuando lo visible se achica, y la hoja
 * mide siempre lo visible mientras esté abierto.
 *
 * OBS-009 (E-13, Chrome en iPhone): el video de Serena era de Chrome, no de
 * Safari. Chrome en iOS no avisa que lo visible se achicó al abrir el teclado
 * (`visualViewport` no cambia), así que para el portal el teclado nunca se
 * abría. Ahora (1) se escucha también el alto de la ventana, y (2) si al tocar
 * un campo no llega ningún aviso, igual se lleva el campo a la mitad de
 * arriba de la pantalla, que ningún teclado tapa.
 */

/** Sin aviso del teclado, el campo se deja por encima de esta fracción de la pantalla. */
const FRACCION_SEGURA = 0.45
/** Cuánto se espera un aviso del teclado después de tocar el campo. */
const ESPERA_AVISO_MS = 450
export function useCampoALaVista() {
  useEffect(() => {
    // matchMedia puede faltar (jsdom de las pruebas unitarias).
    if (typeof window.matchMedia !== 'function') return
    if (!window.matchMedia('(pointer: coarse)').matches) return
    const vista = window.visualViewport
    if (!vista) return
    const raiz = document.documentElement

    let cuadro: number | null = null
    let quieto: ReturnType<typeof setTimeout> | null = null
    let visibleAnterior = window.innerHeight
    let abiertoAntes = false
    // Alto de la ventana sin teclado: Chrome en iOS achica la ventana entera.
    let altoSinTeclado = window.innerHeight
    let sinAviso: ReturnType<typeof setTimeout> | null = null
    // Mientras el teclado está abierto, si el contenido del panel cambia de
    // alto (termina de cargar la imagen de la pieza, llega un comentario), el
    // campo se vuelve a acomodar: si no, quedaba empujado fuera de la vista.
    let vigilado: HTMLElement | null = null
    let reacomodo: number | null = null
    const vigia = new ResizeObserver(() => {
      if (!abiertoAntes) return
      reacomodo ??= requestAnimationFrame(() => {
        reacomodo = null
        acomodarCampo()
      })
    })
    const vigilar = (contenedor: HTMLElement | null) => {
      if (contenedor === vigilado) return
      vigia.disconnect()
      vigilado = contenedor
      if (contenedor) for (const hijo of contenedor.children) vigia.observe(hijo)
    }

    const acomodarCampo = (limiteAbajo = vista.offsetTop + vista.height) => {
      const activo = document.activeElement
      if (!esCampo(activo)) return
      const contenedor = contenedorConScroll(activo)
      vigilar(contenedor)
      const caja = activo.getBoundingClientRect()
      const marco = contenedor?.getBoundingClientRect()
      // Lo que se ve: la parte visible de la página, recortada por el panel.
      const arriba = Math.max(vista.offsetTop, marco?.top ?? -Infinity) + MARGEN
      const abajo = Math.min(limiteAbajo, marco?.bottom ?? Infinity) - MARGEN
      if (caja.top >= arriba && caja.bottom <= abajo) return
      if (!contenedor) {
        activo.scrollIntoView({ block: 'nearest' })
        return
      }
      // Se corre el panel lo justo. scrollIntoView no cuenta el relleno del
      // panel y en Android dejaba el campo 30 px debajo de la hoja (E-12).
      contenedor.scrollTop +=
        caja.bottom > abajo ? caja.bottom - abajo : caja.top - arriba
    }

    const aplicar = () => {
      cuadro = null
      // Si la ventana entera se achicó (Chrome en iOS), eso también es el teclado.
      const { tapado, visible, abierto } = medirTeclado(
        vista,
        window.innerHeight,
        Math.max(window.innerHeight, altoSinTeclado),
      )
      if (abierto) {
        // La hoja se apoya en el borde de abajo de lo visible y mide lo visible.
        raiz.style.setProperty('--alto-teclado', `${String(tapado)}px`)
        raiz.style.setProperty('--alto-visible', `${String(visible)}px`)
      } else {
        raiz.style.removeProperty('--alto-teclado')
        raiz.style.removeProperty('--alto-visible')
        vigilar(null)
      }
      const abriendo = abierto && visible < visibleAnterior
      visibleAnterior = visible
      abiertoAntes = abierto
      if (!abierto) altoSinTeclado = window.innerHeight
      if (abierto && sinAviso) {
        clearTimeout(sinAviso)
        sinAviso = null
      }
      if (quieto) clearTimeout(quieto)
      // Sólo cuando el teclado abre o se agranda; al cerrarse, nada.
      if (abriendo)
        quieto = setTimeout(() => {
          acomodarCampo()
        }, QUIETO_MS)
    }

    const alMoverse = () => {
      cuadro ??= requestAnimationFrame(aplicar)
    }

    const alEnfocar = (e: FocusEvent) => {
      if (!esCampo(e.target)) return
      // El teclado ya estaba abierto (se pasó de un campo a otro).
      if (abiertoAntes) {
        if (quieto) clearTimeout(quieto)
        quieto = setTimeout(() => {
          acomodarCampo()
        }, QUIETO_MS)
        return
      }
      // Sin aviso del teclado (Chrome en iOS): el campo va a la mitad de arriba.
      if (sinAviso) clearTimeout(sinAviso)
      sinAviso = setTimeout(() => {
        sinAviso = null
        if (abiertoAntes) return
        acomodarCampo(vista.offsetTop + window.innerHeight * FRACCION_SEGURA)
      }, ESPERA_AVISO_MS)
    }

    const alCambiarVentana = () => {
      // Al abrir el teclado Chrome achica innerHeight sin tocar visualViewport.
      if (
        window.innerHeight < altoSinTeclado - UMBRAL_TECLADO ||
        window.innerHeight >= altoSinTeclado
      )
        alMoverse()
    }

    document.addEventListener('focusin', alEnfocar)
    vista.addEventListener('resize', alMoverse)
    vista.addEventListener('scroll', alMoverse)
    window.addEventListener('resize', alCambiarVentana)
    return () => {
      document.removeEventListener('focusin', alEnfocar)
      window.removeEventListener('resize', alCambiarVentana)
      if (sinAviso) clearTimeout(sinAviso)
      vista.removeEventListener('resize', alMoverse)
      vista.removeEventListener('scroll', alMoverse)
      if (cuadro) cancelAnimationFrame(cuadro)
      if (reacomodo) cancelAnimationFrame(reacomodo)
      if (quieto) clearTimeout(quieto)
      vigia.disconnect()
      raiz.style.removeProperty('--alto-teclado')
      raiz.style.removeProperty('--alto-visible')
    }
  }, [])
}
